/**
 * Bug Report Hook - Web Version
 *
 * Comprehensive data collection and submission for bug reports.
 * Collects system info, app context, console logs, and screenshots.
 * Adapted for browser environment.
 */

import { createLogger } from '@ctrl-enter/tools/utils';
import {
  AppContext,
  BugCategory,
  BugSeverity,
  ConsoleLogs,
  ScreenshotAttachment,
  SubmitBugReportInput,
  SystemInfo,
} from '@ctrl-enter/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSubmitBugReportMutation } from '../hooks/use-gateway-queries';
import { usePlatform } from '../hooks/use-platform';
import { useScreenshotListener } from '../hooks/use-screenshot-listener';
import { useSessionStore } from '../stores/session-store';
import { getErrorMessage, serializeError } from '../utils/error-utils';

const logger = createLogger('web:hook:use-bug-report');

// Global flag to prevent multiple console wrapping (for HMR)
let consoleAlreadyWrapped = false;

/**
 * Console log capture utility
 * Stores last N console messages for bug reports
 */
class ConsoleCapture {
  private errors: string[] = [];
  private warnings: string[] = [];
  private logs: string[] = [];
  private readonly MAX_ENTRIES = 50;
  private isCapturing = false; // Flag to prevent recursive calls

  constructor() {
    this.setupListeners();
  }

  private setupListeners() {
    // Prevent multiple wrapping (happens during HMR)
    if (consoleAlreadyWrapped) {
      return;
    }
    consoleAlreadyWrapped = true;

    // Store original console methods
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalLog = console.log;

    // Wrap console methods to capture messages
    console.error = (...args: unknown[]) => {
      if (!this.isCapturing) {
        this.isCapturing = true;
        try {
          this.addError(this.formatArgs(args));
        } catch (err) {
          // Silently fail - don't break console if capture fails
        } finally {
          this.isCapturing = false;
        }
      }
      originalError.apply(console, args);
    };

    console.warn = (...args: unknown[]) => {
      if (!this.isCapturing) {
        this.isCapturing = true;
        try {
          this.addWarning(this.formatArgs(args));
        } catch (err) {
          // Silently fail - don't break console if capture fails
        } finally {
          this.isCapturing = false;
        }
      }
      originalWarn.apply(console, args);
    };

    console.log = (...args: unknown[]) => {
      if (!this.isCapturing) {
        this.isCapturing = true;
        try {
          this.addLog(this.formatArgs(args));
        } catch (err) {
          // Silently fail - don't break console if capture fails
        } finally {
          this.isCapturing = false;
        }
      }
      originalLog.apply(console, args);
    };
  }

  private formatArgs(args: unknown[]): string {
    return args
      .map(arg => {
        if (typeof arg === 'string') return arg;
        if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      })
      .join(' ');
  }

  private addError(message: string) {
    this.errors.unshift(`[${new Date().toISOString()}] ${message}`);
    if (this.errors.length > this.MAX_ENTRIES) {
      this.errors.pop();
    }
  }

  private addWarning(message: string) {
    this.warnings.unshift(`[${new Date().toISOString()}] ${message}`);
    if (this.warnings.length > this.MAX_ENTRIES) {
      this.warnings.pop();
    }
  }

  private addLog(message: string) {
    this.logs.unshift(`[${new Date().toISOString()}] ${message}`);
    if (this.logs.length > this.MAX_ENTRIES) {
      this.logs.pop();
    }
  }

  public getLogs(): ConsoleLogs {
    return {
      errors: [...this.errors],
      warnings: [...this.warnings],
      logs: [...this.logs],
    };
  }

  public clear() {
    this.errors = [];
    this.warnings = [];
    this.logs = [];
  }
}

// Global console capture instance
const consoleCapture = new ConsoleCapture();

/**
 * Hook for submitting bug reports
 */
export const useBugReport = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const platform = usePlatform();
  const sessionStore = useSessionStore();
  const submitBugReportMutation = useSubmitBugReportMutation();
  const { screenshot } = useScreenshotListener();

  // Track last screenshot capture
  const lastScreenshotRef = useRef<
    { dataUrl: string; timestamp: number; sizeBytes: number } | undefined
  >();

  useEffect(() => {
    if (screenshot) {
      lastScreenshotRef.current = {
        dataUrl: screenshot.dataUrl,
        timestamp: screenshot.timestamp,
        sizeBytes: new Blob([screenshot.dataUrl]).size,
      };
    }
  }, [screenshot]);

  /**
   * Collect system information
   */
  const collectSystemInfo = useCallback((): SystemInfo => {
    return {
      platform: platform.isMac ? 'darwin' : platform.isWindows ? 'win32' : 'linux',
      platformVersion: navigator.userAgent,
      arch: navigator.platform,
      appVersion: import.meta.env.VITE_APP_VERSION || 'unknown',
      electronVersion: 'N/A (Web)',
      nodeVersion: 'N/A (Web)',
      chromeVersion: navigator.userAgent.match(/Chrome\/(\d+)/)?.[1] || 'unknown',
      environment: (import.meta.env.MODE as 'development' | 'production') || 'development',
      locale: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      totalMemoryMB: (navigator as any).deviceMemory ? (navigator as any).deviceMemory * 1024 : 0,
      usedMemoryMB: 0, // Not available in web
      uptime: 0, // Not tracked in web
      windowSize: `${window.innerWidth}x${window.innerHeight}`,
    };
  }, [platform]);

  /**
   * Collect application context
   */
  const collectAppContext = useCallback((): AppContext => {
    return {
      currentView: 'chat',
      isOnline: navigator.onLine,
      hasActiveSession: true, // Simplified for web
      lastError: consoleCapture.getLogs().errors[0],
      routeHistory: [], // Could be tracked if needed
    };
  }, []);

  /**
   * Collect console logs
   */
  const collectConsoleLogs = useCallback((): ConsoleLogs => {
    return consoleCapture.getLogs();
  }, []);

  /**
   * Capture screenshot (returns last captured screenshot)
   */
  const captureScreenshot = useCallback(async (): Promise<ScreenshotAttachment | undefined> => {
    if (!lastScreenshotRef.current) {
      return undefined;
    }

    // Convert to proper ScreenshotAttachment format
    return {
      mimeType: 'image/png',
      filename: `screenshot-${Date.now()}.png`,
      base64Data:
        lastScreenshotRef.current.dataUrl.split(',')[1] || lastScreenshotRef.current.dataUrl,
      sizeBytes: lastScreenshotRef.current.sizeBytes,
    };
  }, []);

  /**
   * Submit bug report
   */
  const submitBugReport = useCallback(
    async (
      title: string,
      description: string,
      severity: BugSeverity,
      category: BugCategory,
      options?: {
        stepsToReproduce?: string;
        expectedBehavior?: string;
        actualBehavior?: string;
        includeScreenshot?: boolean;
        screenshotAttachment?: ScreenshotAttachment;
      }
    ): Promise<{
      success: boolean;
      error?: string;
      sentryEventId?: string;
    }> => {
      setIsSubmitting(true);

      try {
        logger.info('🐛 [BUG-REPORT] Submitting bug report:', { title, severity, category });

        // Collect all data
        const systemInfo = collectSystemInfo();
        const appContext = collectAppContext();
        const consoleLogs = collectConsoleLogs();

        // Get screenshot if requested
        let screenshot: ScreenshotAttachment | undefined;
        if (options?.screenshotAttachment) {
          screenshot = options.screenshotAttachment;
          logger.info('📸 [BUG-REPORT] Attached screenshot included');
        } else if (options?.includeScreenshot) {
          screenshot = await captureScreenshot();
          if (screenshot) {
            logger.info('📸 [BUG-REPORT] Screenshot attached');
          }
        }

        // Build bug report payload
        const bugReport: SubmitBugReportInput = {
          title,
          description,
          stepsToReproduce: options?.stepsToReproduce,
          expectedBehavior: options?.expectedBehavior,
          actualBehavior: options?.actualBehavior,
          severity,
          category,
          systemInfo,
          appContext,
          consoleLogs,
          screenshot,
          timestamp: new Date().toISOString(),
        };

        // Submit to backend (ts-rest requires { body: ... } format)
        const response = await submitBugReportMutation.mutateAsync({ body: bugReport });

        if (response.status === 200) {
          // Clear console logs after successful submission
          consoleCapture.clear();

          return {
            success: true,
            sentryEventId: response.body.sentryEventId || undefined,
          };
        } else {
          return {
            success: false,
            error: response.body.message || 'Failed to submit bug report',
          };
        }
      } catch (error) {
        // Use error utilities for proper serialization
        const errorDetails = serializeError(error);

        logger.error('❌ [BUG-REPORT] Failed to submit bug report');
        logger.info('❌ [BUG-REPORT] Error details:', errorDetails);

        return {
          success: false,
          error: getErrorMessage(error),
        };
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      collectSystemInfo,
      collectAppContext,
      collectConsoleLogs,
      captureScreenshot,
      submitBugReportMutation,
    ]
  );

  return {
    submitBugReport,
    isSubmitting,
    captureScreenshot,
  };
};
